import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, Eye, Ban } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/cpa-packet/links`;
const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const STATUS_STYLE = { active: 'bg-emerald-100 text-emerald-800', expired: 'bg-stone-200 text-stone-600', revoked: 'bg-red-100 text-red-700' };

export const useReviewLinks = () => {
  const [links, setLinks] = useState([]);
  const load = useCallback(() => {
    axios.get(API, { withCredentials: true }).then(r => setLinks(r.data.links || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return { links, reload: load };
};

export const ReviewLinksList = ({ links, reload }) => {
  const [confirming, setConfirming] = useState(null);
  const revoke = async (id) => {
    setConfirming(null);
    try {
      await axios.delete(`${API}/${id}`, { withCredentials: true });
      toast.success('Link revoked — your CPA can no longer open it');
      reload();
    } catch { toast.error('Could not revoke link'); }
  };
  if (!links.length) return null;
  return (
    <div className="mt-4" data-testid="review-links-list">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-stone-700"><Link2 className="h-3.5 w-3.5" /> Shared review links</p>
      <ul className="divide-y divide-stone-200 rounded-lg border border-stone-200 bg-white">
        {links.map(l => (
          <li key={l.link_id} className="flex flex-wrap items-center gap-3 px-3 py-2.5 text-xs" data-testid={`review-link-${l.link_id}`}>
            <span className={`rounded-full px-2 py-0.5 font-medium ${STATUS_STYLE[l.status]}`} data-testid={`review-link-status-${l.link_id}`}>{l.status}</span>
            <span className="min-w-0 flex-1 truncate text-stone-800">{l.to_email} <span className="text-stone-500">· {l.year} · sent {fmt(l.created_at)} · expires {fmt(l.expires_at)}</span></span>
            <span className="flex items-center gap-1 text-stone-500" data-testid={`review-link-views-${l.link_id}`}>
              <Eye className="h-3.5 w-3.5" /> {l.view_count} view{l.view_count === 1 ? '' : 's'}{l.last_viewed_at && ` · last ${fmt(l.last_viewed_at)}`}
            </span>
            {l.status === 'active' && (
              confirming === l.link_id ? (
                <span className="flex items-center gap-1">
                  <Button size="sm" variant="destructive" onClick={() => revoke(l.link_id)} className="h-7" data-testid={`review-link-confirm-revoke-${l.link_id}`}>Confirm revoke</Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirming(null)} className="h-7">Keep</Button>
                </span>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setConfirming(l.link_id)} className="h-7 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`review-link-revoke-${l.link_id}`}>
                  <Ban className="mr-1 h-3.5 w-3.5" /> Revoke
                </Button>
              )
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ReviewLinksList;
